/*
 * Templating engine — ported from CustomerPortalClient.js.
 * Backend-defined props use #<JS expression># syntax which is evaluated
 * against a scope built from runtime state (staticData, ctx, formData, etc.).
 *
 * Note: evalInScope uses `new Function` + `with` — this is intentional
 * and must match the original behavior exactly for backend schemas to work.
 */

import Cookies from "js-cookie";
import {
    activeCtx,
    staticData,
    refs,
    filtersPersistent,
    responses,
    user_ref,
    formData,
    fetchData,
    s3_active_credentials,
    actions,
    promises,
    dictionary,
} from "./state";

const get_backend_url = (): string => {
    let hosts: Record<string, string> = {};
    try {
        hosts = JSON.parse('{"portal-dev-ai.ewcs.ch":"https://api2.portal.stage.ewcs.ch"}');
    } catch {
        hosts = {};
    }
    if (typeof window !== "undefined") {
        return hosts[window.location.hostname] || "";
    }
    return "";
};

export const base_url = get_backend_url();

export const active_context = () => {
    if (!activeCtx.ctx) {
        return {
            provider: undefined,
            reseller: undefined,
            client: undefined,
            properties: {},
            region: Cookies.get("region"),
            back_end: base_url,
        };
    }
    return {
        provider: activeCtx.ctx.length > 0 ? activeCtx.ctx[0] : undefined,
        reseller: activeCtx.ctx.length > 1 ? activeCtx.ctx[1] : undefined,
        client: activeCtx.ctx.length > 2 ? activeCtx.ctx[2] : undefined,
        properties: activeCtx.properties,
        region: activeCtx.region || Cookies.get("region"),
        back_end: base_url,
    };
};

export const getScope = (context: Record<string, unknown> = {}) => {
    return {
        ...staticData,
        ctx: active_context(),
        refs,
        filtersPersistent,
        ...responses,
        user: user_ref.user,
        formData,
        fetchData,
        ...context,
        s3_active_credentials,
        actions,
        wait_for: (entry: string) => {
            if (!promises[entry]) {
                const prom = { promise: undefined as unknown as Promise<unknown>, resolve: (_value?: unknown) => {}, reject: (_reason?: unknown) => {} };
                prom.promise = new Promise((resolve, reject) => {
                    prom.resolve = resolve;
                    prom.reject = reject;
                });
                promises[entry] = prom as never;
            }
            return promises[entry].promise;
        },
    };
};

export const evalInScope = (js: string, contextAsScope: Record<string, unknown>): unknown => {
    "use strict";
    // eslint-disable-next-line @typescript-eslint/no-implied-eval, no-new-func
    return new Function(`with (this) { return (${js}); }`).call(contextAsScope);
};

/* i18n lookup — forward declaration to avoid circular import */
let _i18n_fn: ((val: string, token?: string | null) => string) | null = null;
export const set_i18n_fn = (fn: (val: string, token?: string | null) => string) => {
    _i18n_fn = fn;
};

/* Toast function — set by the client module to avoid circular deps */
let _toast_fn: ((severity: string, summary: string, detail: string, life?: number) => void) | null = null;
export const set_toast_fn = (fn: (severity: string, summary: string, detail: string, life?: number) => void) => {
    _toast_fn = fn;
};

const _ = (val: string, token: string | null = null): string => {
    if (!val || (staticData.editEnabled as boolean) || !dictionary.isLoaded) {
        return val;
    }
    const r = dictionary[(token || val).toLocaleLowerCase()] as string | undefined;
    if (r) {
        return r;
    }
    if (_i18n_fn) {
        _i18n_fn(val, token);
    }
    return val;
};

export const _t = _;

export const mapValues = (
    val: unknown,
    context: Record<string, unknown>,
    raw = false,
    show_err = true,
    greed = false
): unknown => {
    const scope = getScope(context);

    if (!val) return val;
    if (typeof val !== "string") return val;
    try {
        if (val.indexOf("#") >= 0) {
            let match = val.match(/#(.+?)#/s);
            if (greed) {
                match = val.match(/#(.+)#/s);
            }
            if (!match) return val;
            const [replace, group] = match;
            const result = evalInScope(group, scope);
            if (raw) {
                return result;
            }
            return mapValues(val.replaceAll(replace, String(result)), context, raw, show_err);
        } else {
            return val;
        }
    } catch (error) {
        console.error(error);
        if (show_err) {
            if (_toast_fn) {
                _toast_fn("error", _("Error"), String(error), 600000);
            }
        } else {
            return val;
        }
    }
};
